import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { ReactFlowProvider, useReactFlow } from "@xyflow/react";
import { Eye, FlaskConical, GitBranch, GitCompare, History, LayoutGrid, Layers, Maximize, MousePointerClick, Radio, RotateCcw, Sparkles, SquareDashed, X } from "lucide-react";
import { ApiError, useApi, usePermission } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Tooltip, cn, confirmAnswer, useSave } from "@ncp/ui";
import { repoKeys, repoPaths, useCommit, useDiff, useLog } from "./api";
import { BuilderCanvas, type CanvasOverlay } from "./canvas/BuilderCanvas";
import { BuilderTopBar, type Relation } from "./BuilderTopBar";
import { CopilotPanel, type CopilotProposal } from "./copilot/CopilotPanel";
import { ComparePanel } from "./diff/ComparePanel";
import { compareGraphs, overlayOf } from "./diff/compare";
import { autoLayout, clone, ensurePositions, NODE_W, shortHash } from "./graph";
import { HistoryPanel, reachable } from "./history/HistoryPanel";
import { useAutosave, useLiveValidation } from "./hooks";
import { Inspector } from "./inspector/Inspector";
import { Palette } from "./palette/Palette";
import { SimulatorPanel, type SimTrace } from "./simulator/SimulatorPanel";
import { useBuilder, useStore } from "./store";
import { CommitDialog, NameDialog, PublishDialog, type CommitInputs } from "./vcs/Dialogs";
import { MergeDialog } from "./vcs/MergeDialog";
import type { BranchDetail, CommitView, Graph, GraphChange, RepoView } from "./types";

type Panel = "inspect" | "copilot" | "history" | "test";
const PANELS: Array<{ id: Panel; icon: typeof Eye }> = [{ id: "inspect", icon: MousePointerClick }, { id: "copilot", icon: Sparkles }, { id: "history", icon: History }, { id: "test", icon: FlaskConical }];

export function BuilderWorkspace({ repo, detail, branch, canWrite, headGraph }: { repo: RepoView; detail: BranchDetail; branch: string; canWrite: boolean; headGraph: Graph }) {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can } = usePermission();
  const store = useStore();
  const flow = useReactFlow();
  const graph = useBuilder((s) => s.graph);
  const meta = useBuilder((s) => s.meta);
  const dirty = useBuilder((s) => s.dirty);
  const key = repo.key;
  const head = detail.head.commit;
  const log = useLog(key);
  const commits = useMemo(() => log.data ?? [], [log.data]);

  const [panel, setPanelState] = useState<Panel>((params.get("panel") as Panel) ?? "inspect");
  const setPanel = (p: Panel) => { setPanelState(p); setParams((x) => { x.set("panel", p); return x; }, { replace: true }); };
  const [inspectorTab, setInspectorTab] = useState("general");
  const [viewing, setViewing] = useState<string | null>(null);
  const [compare, setCompare] = useState<{ from: string; to: string; mode: "overlay" | "split" } | null>(null);
  const [proposal, setProposal] = useState<CopilotProposal | null>(null);
  const [ghost, setGhost] = useState(true);
  const [sim, setSim] = useState<SimTrace | null>(null);
  const [heatOn, setHeatOn] = useState(false);
  const [lanesOn, setLanesOn] = useState(false);
  const [uncommitted, setUncommitted] = useState(false);
  const [pendingAi, setPendingAi] = useState<{ prompt: string; summary: string } | null>(null);
  const [dialog, setDialog] = useState<null | { kind: "commit" } | { kind: "branch"; from?: string } | { kind: "tag"; commitId: string } | { kind: "publish"; commitId: string } | { kind: "merge" }>(null);

  const issues = useLiveValidation(graph);
  const autosave = useAutosave(key, branch, head.id, canWrite && !viewing && !compare);
  const viewCommit = useCommit(key, viewing);
  const fromCommit = useCommit(key, compare?.from ?? null);
  const toCommit = useCommit(key, compare?.to ?? null);
  const diff = useDiff(key, compare?.from ?? null, compare?.to ?? null);

  const main = repo.branches.find((b) => b.isProtected);
  const mainSet = useMemo(() => reachable(commits, main?.headCommitId), [commits, main?.headCommitId]);
  const liveCommit = repo.live?.commitId ?? null;
  const viewId = viewing ?? head.id;

  const relation = useMemo<Relation>(() => {
    if (!liveCommit || commits.length === 0) return { kind: "none" };
    if (viewId === liveCommit) return dirty && !viewing ? { kind: "ahead", ahead: 0 } : { kind: "live" };
    const mine = reachable(commits, viewId);
    const theirs = reachable(commits, liveCommit);
    const ahead = [...mine].filter((x) => !theirs.has(x)).length;
    const behind = [...theirs].filter((x) => !mine.has(x)).length;
    return mine.has(liveCommit) ? { kind: "ahead", ahead } : theirs.has(viewId) ? { kind: "behind", behind } : { kind: "diverged", ahead, behind };
  }, [commits, viewId, liveCommit, dirty, viewing]);

  const invalidate = useCallback(async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: repoKeys.repo(key) }), qc.invalidateQueries({ queryKey: repoKeys.log(key) }), qc.invalidateQueries({ queryKey: repoKeys.gallery })]);
  }, [qc, key]);
  const goBranch = useCallback((name: string) => { setParams((x) => { x.set("branch", name); return x; }); }, [setParams]);

  // ---------------------------------------------------------------- mutations

  // Every mutation below reports its failure itself (useSave), with the cause and the server's words.
  const commit = useSave({
    mutationFn: (i: CommitInputs) => api.post<{ commit: CommitView; branch: string; merged?: CommitView | null }>(repoPaths.commits(key, branch), {
      graph, message: i.message, expectedHead: head.id, source: pendingAi ? "Ai" : "Manual", copilotPrompt: pendingAi?.prompt ?? null,
      meta: { name: meta.name, nameAr: meta.nameAr, description: meta.description, legalBasis: meta.legalBasis }, newBranch: i.newBranch ?? null, mergeToMain: i.mergeToMain,
    }),
    saved: (r) => ({ title: r.merged ? t("admin:builder.commit.doneMerged", { branch: r.branch }) : t("admin:builder.commit.done", { branch: r.branch }), description: r.commit.summary ?? undefined }),
    failed: (e) => (e instanceof ApiError && e.status === 409 ? t("admin:builder.commit.conflict") : t("admin:builder.commit.failed")),
    onSuccess: async (r) => {
      setDialog(null);
      setPendingAi(null);
      // The commit is stored; clearing the old branch's working copy is tidying only (the work is in the commit), so its failure is not
      // reported as a failed commit.
      if (r.branch !== branch) await api.delete(repoPaths.workingCopy(key, branch)).catch(() => {});
      await invalidate();
      await qc.invalidateQueries({ queryKey: ["workflow-repos", key, "branch"] });
      if (r.branch !== branch) goBranch(r.branch);
    },
  });

  const createBranch = useSave({
    mutationFn: (v: { name: string; from?: string; note?: string }) => api.post<{ name: string }>(`${repoPaths.base(key)}/branches`, { name: v.name, from: v.from ?? branch, description: v.note || null }),
    saved: (b) => t("admin:builder.branches.created", { name: b.name }),
    onSuccess: async (b) => { setDialog(null); await invalidate(); goBranch(b.name); },
  });

  /** Restore / revert need an unprotected branch: on main they go to a new branch first. */
  const onBranchThen = useSave({
    mutationFn: async (v: { action: "restore" | "revert"; commitId: string }) => {
      let target = branch;
      let expected: string | undefined = head.id;
      if (detail.branch.isProtected) {
        const created = confirmAnswer(await api.post<{ name: string; headCommitId: string }>(`${repoPaths.base(key)}/branches`, { name: `${v.action}-${shortHash(commits.find((c) => c.id === v.commitId))}`, from: head.id }), "record");
        target = created.name;
        expected = created.headCommitId;
      }
      // Both steps must be confirmed by the API's own answer before "restored" / "reverted" shows.
      confirmAnswer(await api.post(`${repoPaths.base(key)}/${v.action}`, { branch: target, commitId: v.commitId, expectedHead: expected }));
      return { target };
    },
    saved: ({ target }, v) => (v.action === "restore" ? t("admin:builder.history.restored", { branch: target }) : t("admin:builder.history.reverted", { branch: target })),
    onSuccess: async ({ target }) => {
      setViewing(null);
      await invalidate();
      await qc.invalidateQueries({ queryKey: ["workflow-repos", key, "branch"] });
      if (target !== branch) goBranch(target);
    },
  });

  const tag = useSave({
    mutationFn: (v: { commitId: string; name: string; note: string }) => api.post(`${repoPaths.base(key)}/tags`, { name: v.name, commitId: v.commitId, message: v.note || null }),
    saved: t("admin:builder.history.tagged"),
    onSuccess: async () => { setDialog(null); await invalidate(); },
  });

  const publish = useSave({
    mutationFn: (v: { commitId: string; reason: string }) => api.post<{ changeSetId: string; title: string; version: number; href: string }>(`${repoPaths.base(key)}/publish`, { commitId: v.commitId, reason: v.reason || null }),
    saved: (r) => ({ title: t("admin:builder.publish.staged", { version: r.version }), description: r.title, action: { label: t("admin:builder.publish.openRelease"), onClick: () => navigate(r.href) } }),
    onSuccess: async () => {
      setDialog(null);
      await invalidate();
    },
  });

  // ---------------------------------------------------------------- keyboard

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); if (canWrite && store.getState().dirty) setDialog({ kind: "commit" }); }
      if (e.key === "Escape") { setViewing(null); setCompare(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canWrite, store]);

  // ---------------------------------------------------------------- what the canvas shows

  const viewGraph = useMemo(() => (viewCommit.data ? ensurePositions(clone(viewCommit.data.graph)) : null), [viewCommit.data]);
  const fromGraph = useMemo(() => (fromCommit.data ? ensurePositions(clone(fromCommit.data.graph)) : null), [fromCommit.data]);
  const toGraph = useMemo(() => (toCommit.data ? ensurePositions(clone(toCommit.data.graph)) : null), [toCommit.data]);
  const previewGraph = useMemo(() => (proposal?.preview && ghost ? ensurePositions(clone(proposal.preview)) : null), [proposal, ghost]);

  const scene = useMemo((): { graph?: Graph; overlay?: CanvasOverlay; editable: boolean; banner: "view" | "compare" | "proposal" | null } => {
    if (compare && fromGraph && toGraph) return { graph: toGraph, overlay: overlayOf(compareGraphs(fromGraph, toGraph)), editable: false, banner: "compare" };
    if (viewing && viewGraph) return { graph: viewGraph, editable: false, banner: "view", overlay: heatOn && viewing === liveCommit ? { heat: repo.live?.casesByState } : undefined };
    if (previewGraph) return { graph: previewGraph, overlay: overlayOf(compareGraphs(graph, previewGraph)), editable: false, banner: "proposal" };
    const overlay: CanvasOverlay = {};
    if (uncommitted) Object.assign(overlay, overlayOf(compareGraphs(headGraph, graph)));
    if (heatOn) overlay.heat = repo.live?.casesByState;
    if (sim) { overlay.simCurrent = sim.current; overlay.simVisited = sim.visited; overlay.simEdges = sim.edges; }
    return { overlay, editable: canWrite, banner: null };
  }, [compare, fromGraph, toGraph, viewing, viewGraph, previewGraph, graph, uncommitted, headGraph, heatOn, sim, canWrite, repo.live, liveCommit]);

  const focusChange = (c: GraphChange) => {
    const g = scene.graph ?? graph;
    const n = c.entity === "node" ? g.nodes.find((x) => x.id === c.id) ?? fromGraph?.nodes.find((x) => x.id === c.id) : g.nodes.find((x) => x.key === g.edges.find((e) => e.id === c.id)?.from);
    if (n?.position) flow.setCenter(n.position.x + NODE_W / 2, n.position.y + 40, { zoom: 1.1, duration: 500 });
  };

  const historyActions = {
    onView: (id: string) => { setCompare(null); setViewing(id === head.id && !dirty ? null : id); },
    onCompare: (from: string, to: string) => { setViewing(null); setCompare({ from, to, mode: "overlay" }); },
    onRestore: canWrite || can("platform.workflows.write") ? (id: string) => onBranchThen.mutate({ action: "restore", commitId: id }) : undefined,
    onRevert: can("platform.workflows.write") ? (id: string) => onBranchThen.mutate({ action: "revert", commitId: id }) : undefined,
    onBranchFrom: can("platform.workflows.write") ? (id: string) => setDialog({ kind: "branch", from: id }) : undefined,
    onTag: can("platform.workflows.write") ? (id: string) => setDialog({ kind: "tag", commitId: id }) : undefined,
    onPublish: can("platform.workflows.publish") ? (id: string) => setDialog({ kind: "publish", commitId: id }) : undefined,
  };

  const canPublishHead = can("platform.workflows.publish") && detail.branch.isProtected && head.id !== liveCommit && !repo.pending.some((p) => p.commitId === head.id);
  const publishTarget = dialog?.kind === "publish" ? commits.find((c) => c.id === dialog.commitId) : undefined;
  const comparison = useMemo(() => compareGraphs(headGraph, graph), [headGraph, graph]);
  const viewed = commits.find((c) => c.id === viewing);

  return (
    <>
      <BuilderTopBar
        repo={repo} branch={branch} relation={relation} liveHash={liveCommit ? shortHash(commits.find((c) => c.id === liveCommit)) : null} issues={issues} autosave={autosave} canWrite={canWrite} canPublish={canPublishHead}
        onSwitchBranch={(b) => { setViewing(null); setCompare(null); goBranch(b); }} onNewBranch={() => setDialog({ kind: "branch" })}
        onCommit={() => setDialog({ kind: "commit" })} onMerge={() => setDialog({ kind: "merge" })} onPublish={canPublishHead ? () => setDialog({ kind: "publish", commitId: head.id }) : null}
        onShowLive={() => { if (liveCommit) { setCompare(null); setViewing(liveCommit === head.id ? null : liveCommit); setHeatOn(true); } }}
        readOnlyReason={!can("platform.workflows.write") ? t("admin:builder.readOnly") : detail.branch.status !== "Open" ? t(`admin:builder.branches.status.${detail.branch.status}`) : null}
      />
      <div className="flex min-h-0 flex-1">
        <Palette editable={scene.editable} />
        <main className="relative min-w-0 flex-1">
          {compare?.mode === "split" && fromGraph && toGraph ? (
            <div className="grid h-full grid-cols-2 divide-x divide-sand-200">
              <SplitPane label={commits.find((c) => c.id === compare.from)?.message ?? ""} graph={fromGraph} overlay={overlayOf(compareGraphs(toGraph, fromGraph))} />
              <SplitPane label={commits.find((c) => c.id === compare.to)?.message ?? ""} graph={toGraph} overlay={overlayOf(compareGraphs(fromGraph, toGraph))} />
            </div>
          ) : (
            <BuilderCanvas graph={scene.graph} editable={scene.editable} overlay={scene.overlay} issues={scene.graph ? viewCommit.data?.issues : issues} showLanes={lanesOn}
              onOpenHistory={(id) => { store.select({ nodes: [id], edges: [] }); setPanel("inspect"); setInspectorTab("history"); }}
              onFocusInspector={(tab) => { setPanel("inspect"); setInspectorTab(tab); }} />
          )}

          <div className="pointer-events-none absolute inset-x-0 top-3 flex justify-center px-3">
            {scene.banner === "view" && viewed ? (
              <Banner tone="info">
                <Eye className="size-4" /> <span className="max-w-md truncate">{t("admin:builder.viewing", { commit: viewed.tags[0] ?? shortHash(viewed), message: viewed.message })}</span>
                {viewed.isLive ? <Badge size="sm" variant="success"><Radio /> LIVE</Badge> : null}
                <Button size="sm" variant="outline" onClick={() => historyActions.onCompare(viewed.id, head.id)}><GitCompare /> {t("admin:builder.history.compareHead")}</Button>
                {historyActions.onRestore && viewed.id !== head.id ? <Button size="sm" variant="outline" loading={onBranchThen.isPending} onClick={() => historyActions.onRestore!(viewed.id)}><RotateCcw /> {t("admin:builder.history.restore")}</Button> : null}
                <Button size="sm" variant="ghost" onClick={() => setViewing(null)}><X /> {t("admin:builder.backToBranch", { branch })}</Button>
              </Banner>
            ) : scene.banner === "compare" ? (
              <Banner tone="info"><GitCompare className="size-4" /> {t("admin:builder.comparing")} <Button size="sm" variant="ghost" onClick={() => setCompare(null)}><X /> {t("admin:builder.exitCompare")}</Button></Banner>
            ) : scene.banner === "proposal" && proposal ? (
              <Banner tone="ai"><Sparkles className="size-4" /> {t("admin:builder.copilot.previewing", { summary: proposal.changes?.summary ?? "" })}<Button size="sm" variant="ghost" onClick={() => setGhost(false)}>{t("admin:builder.copilot.hideGhost")}</Button></Banner>
            ) : null}
          </div>

          {!scene.banner ? (
            <div className="absolute start-3 top-3 flex items-center gap-1 rounded-xl border border-sand-200 bg-white/90 p-1 shadow-sand backdrop-blur">
              {canWrite ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button size="sm" variant="ghost"><LayoutGrid /> {t("admin:builder.toolbar.layout")}</Button></DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onSelect={() => store.replace(autoLayout(graph, "LR"), null, "Auto-layout")}>{t("admin:builder.menu.layoutLR")}</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => store.replace(autoLayout(graph, "TB"), null, "Auto-layout")}>{t("admin:builder.menu.layoutTB")}</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => { store.replace(autoLayout(graph, "lanes"), null, "Auto-layout"); setLanesOn(true); }}>{t("admin:builder.menu.layoutLanes")}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              <Toggle on={lanesOn} onClick={() => setLanesOn((v) => !v)} icon={Layers} label={t("admin:builder.toolbar.lanes")} />
              {repo.live ? <Toggle on={heatOn} onClick={() => setHeatOn((v) => !v)} icon={Radio} label={t("admin:builder.toolbar.live", { count: repo.live.runningCases })} /> : null}
              {canWrite ? <Toggle on={uncommitted} onClick={() => setUncommitted((v) => !v)} icon={SquareDashed} label={t("admin:builder.toolbar.changes", { count: comparison.added + comparison.modified + comparison.removed })} /> : null}
              <Tooltip content={t("admin:builder.toolbar.fit")}><Button size="icon-sm" variant="ghost" onClick={() => flow.fitView({ padding: 0.15, duration: 400 })}><Maximize /></Button></Tooltip>
            </div>
          ) : null}
        </main>

        <aside className="flex h-full w-[380px] shrink-0 flex-col border-s border-sand-200 bg-white/95">
          <div className="flex shrink-0 gap-0.5 border-b border-sand-200 p-1.5">
            {PANELS.map(({ id, icon: Icon }) => (
              <button key={id} type="button" onClick={() => { setPanel(id); if (id !== "copilot") setProposal(null); }} className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition", panel === id && !compare ? "bg-aeblack-950 text-white shadow" : "text-sand-700 hover:bg-sand-100")}>
                <Icon className="size-3.5" />{t(`admin:builder.panels.${id}`)}
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 [&>aside]:w-full [&>aside]:border-0">
            {compare ? (
              <ComparePanel diff={diff.data} loading={diff.isLoading} from={commits.find((c) => c.id === compare.from)} to={commits.find((c) => c.id === compare.to)} mode={compare.mode} onMode={(mode) => setCompare({ ...compare, mode })} onClose={() => setCompare(null)} onFocus={focusChange} />
            ) : panel === "inspect" ? (
              <Inspector editable={scene.editable} issues={scene.graph ? viewCommit.data?.issues ?? [] : issues} tab={inspectorTab} onTab={setInspectorTab} repoKey={key} />
            ) : panel === "copilot" ? (
              <CopilotPanel repoKey={key} proposal={proposal} onProposal={(p) => { setProposal(p); setGhost(true); }} ghost={ghost} onGhost={setGhost}
                onAccepted={(prompt, summary) => { setPendingAi({ prompt, summary }); setDialog({ kind: "commit" }); }} diffRange={null}
                initial={params.get("generate") ? { mode: "generate", text: params.get("generate")! } : undefined}
                onInitialConsumed={() => setParams((x) => { x.delete("generate"); return x; }, { replace: true })} />
            ) : panel === "history" ? (
              <div className="h-full overflow-y-auto">
                <div className="flex items-center justify-between border-b border-sand-200 p-3">
                  <div><p className="text-sm font-semibold">{t("admin:builder.history.title")}</p><p className="text-[11px] text-muted-foreground">{t("admin:builder.history.subtitle", { count: commits.length, tags: repo.tags.length })}</p></div>
                  {can("platform.workflows.write") ? <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "branch" })}><GitBranch /> {t("admin:builder.branches.new")}</Button> : null}
                </div>
                <HistoryPanel commits={commits} loading={log.isLoading} viewing={viewing} headId={head.id} liveCommitId={liveCommit} mainCommits={mainSet} actions={historyActions} />
              </div>
            ) : (
              <SimulatorPanel onTrace={setSim} real={canWrite ? { repoKey: key, branch, headCommitId: head.id, dirty } : undefined} />
            )}
          </div>
        </aside>
      </div>

      <CommitDialog open={dialog?.kind === "commit"} onOpenChange={(o) => !o && setDialog(null)} busy={commit.isPending} protectedBranch={detail.branch.isProtected} branch={branch}
        comparison={comparison} issues={issues} initialMessage={pendingAi?.summary} onCommit={(i) => commit.mutate(i)} />
      <NameDialog open={dialog?.kind === "branch"} onOpenChange={(o) => !o && setDialog(null)} busy={createBranch.isPending} title={t("admin:builder.branches.new")} withNote
        description={t("admin:builder.branches.newHint", { from: dialog?.kind === "branch" && dialog.from ? shortHash(commits.find((c) => c.id === dialog.from)) : branch })} label={t("admin:builder.branches.name")}
        onSubmit={(name, note) => createBranch.mutate({ name, from: dialog?.kind === "branch" ? dialog.from : undefined, note })} />
      <NameDialog open={dialog?.kind === "tag"} onOpenChange={(o) => !o && setDialog(null)} busy={tag.isPending} title={t("admin:builder.history.tag")} label={t("admin:builder.history.tagName")} icon="tag" withNote
        onSubmit={(name, note) => dialog?.kind === "tag" && tag.mutate({ commitId: dialog.commitId, name, note })} />
      <PublishDialog open={dialog?.kind === "publish"} onOpenChange={(o) => !o && setDialog(null)} busy={publish.isPending}
        commitLabel={publishTarget ? `${shortHash(publishTarget)} – ${publishTarget.message}` : ""} issues={publishTarget?.id === head.id ? detail.head.issues : []}
        runningCases={repo.live?.runningCases ?? 0} liveVersion={repo.live?.version} onPublish={(reason) => dialog?.kind === "publish" && publish.mutate({ commitId: dialog.commitId, reason })} />
      <MergeDialog open={dialog?.kind === "merge"} onOpenChange={(o) => !o && setDialog(null)} repoKey={key} branches={repo.branches} source={branch}
        onMerged={async (target) => { await invalidate(); await qc.invalidateQueries({ queryKey: ["workflow-repos", key, "branch"] }); goBranch(target); }} />
    </>
  );
}

function SplitPane({ label, graph, overlay }: { label: string; graph: Graph; overlay: CanvasOverlay }) {
  return (
    <div className="relative h-full">
      <p className="absolute start-3 top-3 z-10 max-w-[80%] truncate rounded-lg bg-white/90 px-2 py-1 text-xs font-medium shadow-sand">{label}</p>
      <ReactFlowProvider><BuilderCanvas graph={graph} editable={false} overlay={overlay} minimap={false} /></ReactFlowProvider>
    </div>
  );
}

function Banner({ children, tone }: { children: React.ReactNode; tone: "info" | "ai" }) {
  return <div className={cn("pointer-events-auto flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-sand-lg backdrop-blur", tone === "ai" ? "border-seablue-200 bg-seablue-50/95 text-seablue-900" : "border-aegold-200 bg-white/95 text-aeblack-900")}>{children}</div>;
}

function Toggle({ on, onClick, icon: Icon, label }: { on: boolean; onClick: () => void; icon: typeof Eye; label: string }) {
  return <Button size="sm" variant={on ? "secondary" : "ghost"} onClick={onClick} className={cn(on && "text-aegold-800")}><Icon /> <span className="hidden xl:inline">{label}</span></Button>;
}
